import { useCallback, useEffect, useRef, useState } from "react";
import client from "../api/client";
import { saveTeamOptions, loadTeamOptions } from "../db/TeamDB";

// Fetch-once-then-cache team dropdown options, mirroring CanvassMap.js's
// getCityOptions pattern (fetch -> map to {value,label} -> cache). Refetches
// whenever the active tenant (currentTenantId in localStorage) changes, since
// a cached team list is only ever valid for the tenant it was fetched under.
function useTeamOptions() {
  const [teamOptions, setTeamOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const lastTenantId = useRef(null);

  const fetchTeamOptions = useCallback(async () => {
    const tenantId = localStorage.getItem("currentTenantId");
    setIsLoading(true);
    try {
      const cached = await loadTeamOptions(tenantId);
      if (cached.length > 0) {
        setTeamOptions(cached);
      }

      const response = await client.get("/teams");
      const options = (response.data || []).map((team) => ({
        value: team.id,
        label: team.name,
      }));
      setTeamOptions(options);
      saveTeamOptions(tenantId, options);
    } catch (error) {
      console.error("Error fetching team options:", error);
    } finally {
      setIsLoading(false);
      lastTenantId.current = tenantId;
    }
  }, []);

  useEffect(() => {
    fetchTeamOptions();
  }, [fetchTeamOptions]);

  useEffect(() => {
    const handleStorageChange = () => {
      const tenantId = localStorage.getItem("currentTenantId");
      if (tenantId !== lastTenantId.current) {
        fetchTeamOptions();
      }
    };
    // Storage events only fire across tabs, not within the same tab that made
    // the change — the tenant-switch flow (useTenantSwitcher) does a full
    // page reload after writing currentTenantId, so this hook remounts fresh
    // on that path anyway. This listener exists for the cross-tab case.
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [fetchTeamOptions]);

  return { teamOptions, isLoading, refetchTeamOptions: fetchTeamOptions };
}

export default useTeamOptions;
